// Utility to find and deduplicate all assets/suppliers linked to a data type record
import { getAssets } from '@/services/assetsService';
import { DataRetentionRecordUI } from './dataTypeMapping';


/**
 * Given an asset name, returns the assetID as a string, or null if not found.
 */
async function assetNameToId(name: string): Promise<string | null> {
    const allAssets = await getAssets();
    const found = allAssets.find(a => a.assetDesc === name);
    return found ? String(found.assetID) : null;
}

/**
 * Given a data retention record, finds all assets/suppliers that reference this data type in their dataTypesShared array.
 * Combines these with any assets/suppliers already linked in the record (new way), deduplicates, and returns the full list.
 * @param record DataRetentionRecordUI
 * @returns Promise<string[]> - deduplicated list of asset/supplier names
 */
async function findLinkedAssetsAndSuppliers(record: DataRetentionRecordUI): Promise<string[]> {
    const allAssets = await getAssets();
    const idToName = new Map(allAssets.map(a => [a.assetID, a.assetDesc]));
    const nameSet = new Set(allAssets.map(a => a.assetDesc));
    const allAssetIds = new Set(allAssets.map(a => a.assetID));

    // Helper: Try to extract asset name from a string
    function resolveAssetName(val: string): string | null {
        if (idToName.has(val)) return idToName.get(val)!;
        if (nameSet.has(val)) return val;
        // Try to match concatenated name+id or id+name
        for (const asset of allAssets) {
            if (
                val === `${asset.assetDesc}${asset.assetID}` ||
                val === `${asset.assetID}${asset.assetDesc}`
            ) {
                return asset.assetDesc;
            }
        }
        // Try to extract id from within the string
        for (const asset of allAssets) {
            if (typeof val === 'string' && val.includes(asset.assetID)) return asset.assetDesc;
        }
        // Try to extract name from within the string
        for (const asset of allAssets) {
            if (typeof val === 'string' && val.includes(asset.assetDesc)) return asset.assetDesc;
        }
        return null;
    }

    // Find assets that reference this data type by name in their dataTypesShared array (legacy)
    const legacyLinked = allAssets.filter(asset =>
        Array.isArray(asset.dataTypesShared) && asset.dataTypesShared.includes(record.dataTypeName)
    ).map(asset => asset.assetDesc);

    // Map explicit links to names only if they exist in the asset list
    const explicitLinked = [
        ...(record.assets || []),
        ...(record.suppliers || [])
    ].map(resolveAssetName).filter((x): x is string => !!x);

    // Combine and deduplicate
    return Array.from(new Set([...legacyLinked, ...explicitLinked]));
}

/**
 * Resolves an array of asset/system IDs or names to their display names.
 * Useful for displaying systems assigned to data pools.
 * @param systemIds Array of system IDs or names
 * @returns Promise<string[]> - resolved asset/system names
 */
async function resolveSystemNames(systemIds: string[]): Promise<string[]> {
    if (!systemIds || systemIds.length === 0) return [];
    
    const allAssets = await getAssets();
    const idToName = new Map(allAssets.map(a => [String(a.assetID), a.assetDesc]));
    const nameSet = new Set(allAssets.map(a => a.assetDesc));
    
    const resolved: string[] = [];
    for (const val of systemIds) {
        // Check if it's an ID
        if (idToName.has(val)) {
            resolved.push(idToName.get(val)!);
        } 
        // Check if it's already a name
        else if (nameSet.has(val)) {
            resolved.push(val);
        }
        // Try to find partial match
        else {
            const found = allAssets.find(a => 
                String(a.assetID) === val || 
                a.assetDesc === val ||
                (typeof val === 'string' && val.includes(String(a.assetID))) ||
                (typeof val === 'string' && val.includes(a.assetDesc))
            );
            if (found) {
                resolved.push(found.assetDesc);
            }
        }
    }
    
    return Array.from(new Set(resolved));
}

/**
 * Creates a map of asset IDs to names for efficient lookups.
 * @returns Promise<Map<string, string>> - map of assetID -> assetDesc
 */
async function getAssetIdToNameMap(): Promise<Map<string, string>> {
    const allAssets = await getAssets();
    return new Map(allAssets.map(a => [String(a.assetID), a.assetDesc]));
}

/**
 * Creates a map of asset names to IDs for efficient lookups.
 * @returns Promise<Map<string, string>> - map of assetDesc -> assetID
 */
async function getAssetNameToIdMap(): Promise<Map<string, string>> {
    const allAssets = await getAssets();
    return new Map(allAssets.map(a => [a.assetDesc, String(a.assetID)]));
}

/**
 * Converts an array of system names or IDs to IDs.
 * Handles mixed arrays where some values are names and some are IDs.
 * @param systems Array of system names or IDs
 * @returns Promise<string[]> - array of asset IDs
 */
async function resolveSystemIds(systems: string[]): Promise<string[]> {
    if (!systems || systems.length === 0) return [];
    
    const allAssets = await getAssets();
    const idSet = new Set(allAssets.map(a => String(a.assetID)));
    const nameToId = new Map(allAssets.map(a => [a.assetDesc.toLowerCase(), String(a.assetID)]));
    
    const resolved: string[] = [];
    for (const val of systems) {
        // Check if it's already an ID
        if (idSet.has(val)) {
            resolved.push(val);
        } 
        // Check if it's a name (case-insensitive)
        else if (nameToId.has(val.toLowerCase())) {
            resolved.push(nameToId.get(val.toLowerCase())!);
        }
        // Try exact name match
        else {
            const found = allAssets.find(a => 
                a.assetDesc === val || 
                a.assetDesc.toLowerCase() === val.toLowerCase()
            );
            if (found) {
                resolved.push(String(found.assetID));
            }
        }
    }
    
    return Array.from(new Set(resolved));
}

export default {
    findLinkedAssetsAndSuppliers,
    assetNameToId,
    resolveSystemNames,
    getAssetIdToNameMap,
    getAssetNameToIdMap,
    resolveSystemIds
}
export {
    findLinkedAssetsAndSuppliers,
    assetNameToId,
    resolveSystemNames,
    getAssetIdToNameMap,
    getAssetNameToIdMap,
    resolveSystemIds
}